import { Injectable } from "../../../lib/web/reflection/injectable";
import { Key } from "../../../lib/web/core/key";

@Injectable({ type: 'singleton' })
export class CardNavigationService {

    private _keydownHandler: (event: KeyboardEvent) => void = null;
    private _sliderObservers: WeakMap<HTMLElement, MutationObserver> = new WeakMap();
    private readonly _tabindexRestoreSentinel: string = '__null__';
    
    public constructor() {
        this._keydownHandler = this.handleKeyDown.bind(this);
    }

    public enableGridNavigation(gridElement: HTMLElement): void {
        this.disableNavigation(gridElement);
        gridElement.addEventListener('keydown', this._keydownHandler);
        gridElement.dataset.cardNavigation = 'grid';
    }

    public enableRowNavigation(rowElement: HTMLElement): void {
        this.disableNavigation(rowElement);
        rowElement.addEventListener('keydown', this._keydownHandler);
        rowElement.dataset.cardNavigation = 'row';

        if (rowElement.classList.contains('o-slider')) {
            this.enableSliderTabManagement(rowElement);
        }
    }

    public disableNavigation(element: HTMLElement): void {
        element.removeEventListener('keydown', this._keydownHandler);
        delete element.dataset.cardNavigation;

        const observer: MutationObserver | undefined = this._sliderObservers.get(element);
        if (observer) {
            observer.disconnect();
            this._sliderObservers.delete(element);
        }
    }

    private handleKeyDown(event: KeyboardEvent): void {
        const currentTarget: HTMLElement | null = event.currentTarget as HTMLElement | null;
        if (currentTarget && currentTarget.classList.contains('o-slider')) {
            if (this.handleSliderKeyDown(currentTarget, event)) {
                return;
            }
        }

        const target: HTMLElement = event.target as HTMLElement;
        const cardElement: HTMLElement = this.findCardElement(target);
        if (!cardElement) {
            return;
        }

        const container: HTMLElement = this.findNavigationContainer(cardElement);
        if (!container) {
            return;
        }

        if (currentTarget && currentTarget !== container) {
            return;
        }

        const navigationType: string = container.dataset.cardNavigation;
        const isLink: boolean = cardElement.tagName === 'A';
        if (!isLink && !cardElement.hasAttribute('tabindex')) {
            return;
        }

        switch (event.key) {
            case Key.ArrowLeft:
                event.preventDefault();
                this.navigateToCard(container, cardElement, 'left', navigationType);
                break;
            case Key.ArrowRight:
                event.preventDefault();
                this.navigateToCard(container, cardElement, 'right', navigationType);
                break;
            case Key.ArrowUp:
                if (navigationType === 'grid') {
                    event.preventDefault();
                    this.navigateToCard(container, cardElement, 'up', navigationType);
                }
                else if (navigationType === 'row') {
                    event.preventDefault();
                    this.navigateRowVertical(container, cardElement, 'up');
                }
                break;
            case Key.ArrowDown:
                if (navigationType === 'grid') {
                    event.preventDefault();
                    this.navigateToCard(container, cardElement, 'down', navigationType);
                }
                else if (navigationType === 'row') {
                    event.preventDefault();
                    this.navigateRowVertical(container, cardElement, 'down');
                }
                break;
        }
    }

    private enableSliderTabManagement(sliderElement: HTMLElement): void {
        this.updateSliderTabbables(sliderElement);

        const observer: MutationObserver = new MutationObserver(() => {
            this.updateSliderTabbables(sliderElement);
        });
        observer.observe(sliderElement, {
            subtree: true,
            attributes: true,
            attributeFilter: ['data-active']
        });
        this._sliderObservers.set(sliderElement, observer);
    }

    private handleSliderKeyDown(sliderElement: HTMLElement, event: KeyboardEvent): boolean {
        const target: HTMLElement | null = event.target as HTMLElement | null;
        if (!target) {
            return false;
        }

        if (event.key === 'Tab') {
            return this.handleSliderTabKey(sliderElement, target, event);
        }

        if (event.key === Key.ArrowLeft || event.key === Key.ArrowRight) {
            const bullet: HTMLButtonElement | null = target.closest('.o-slider__bullet') as HTMLButtonElement | null;
            if (!bullet) {
                return false;
            }
            event.preventDefault();
            const direction: 'previous' | 'next' = event.key === Key.ArrowLeft ? 'previous' : 'next';
            this.navigateSliderByBullets(sliderElement, direction);
            return true;
        }

        return false;
    }

    private handleSliderTabKey(sliderElement: HTMLElement, target: HTMLElement, event: KeyboardEvent): boolean {
        const activeSlide: HTMLElement | null = this.getActiveSliderSlide(sliderElement);
        if (!activeSlide) {
            return false;
        }

        const focusables: HTMLElement[] = this.getSliderSlideFocusableElements(activeSlide);
        const activeBullet: HTMLButtonElement | null = this.getActiveSliderBullet(sliderElement);

        if (!focusables.length) {
            return false;
        }

        const first: HTMLElement = focusables[0];
        const last: HTMLElement = focusables[focusables.length - 1];

        if (!event.shiftKey) {
            if (target === last && activeBullet) {
                event.preventDefault();
                activeBullet.focus();
                return true;
            }
            return false;
        }

        if (activeBullet && target === activeBullet) {
            event.preventDefault();
            last.focus();
            return true;
        }

        if (target === first) {
            return false;
        }

        return false;
    }

    private navigateSliderByBullets(sliderElement: HTMLElement, direction: 'previous' | 'next'): void {
        const bullets: HTMLButtonElement[] = Array.from(sliderElement.querySelectorAll('.o-slider__bullet')) as HTMLButtonElement[];
        if (!bullets.length) {
            return;
        }

        const activeBullet: HTMLButtonElement | null = this.getActiveSliderBullet(sliderElement);
        const activeIndex: number = activeBullet ? bullets.indexOf(activeBullet) : 0;
        const nextIndex: number = direction === 'next'
            ? (activeIndex + 1) % bullets.length
            : (activeIndex - 1 + bullets.length) % bullets.length;

        const nextBullet: HTMLButtonElement = bullets[nextIndex];
        nextBullet.click();
        setTimeout(() => {
            this.updateSliderTabbables(sliderElement);
            const updatedActiveBullet: HTMLButtonElement | null = this.getActiveSliderBullet(sliderElement);
            (updatedActiveBullet || nextBullet).focus();
        }, 0);
    }

    private updateSliderTabbables(sliderElement: HTMLElement): void {
        const activeSlide: HTMLElement | null = this.getActiveSliderSlide(sliderElement);
        const slides: HTMLElement[] = Array.from(sliderElement.querySelectorAll('.o-slider__slide')) as HTMLElement[];

        slides.forEach(slide => {
            slide.setAttribute('tabindex', '-1');
        });

        slides.forEach(slide => {
            const isActive: boolean = !!activeSlide && slide === activeSlide;
            const focusables: HTMLElement[] = this.getSliderSlideFocusableElements(slide);
            focusables.forEach(element => {
                if (isActive) {
                    const stored: string | undefined = element.dataset['sliderOrigTabindex'];
                    if (stored !== undefined) {
                        if (stored === this._tabindexRestoreSentinel) {
                            element.removeAttribute('tabindex');
                        }
                        else {
                            element.setAttribute('tabindex', stored);
                        }
                        delete element.dataset['sliderOrigTabindex'];
                    }
                    return;
                }

                if (element.dataset['sliderOrigTabindex'] === undefined) {
                    const original: string | null = element.getAttribute('tabindex');
                    element.dataset['sliderOrigTabindex'] = original ?? this._tabindexRestoreSentinel;
                }
                element.setAttribute('tabindex', '-1');
            });
        });

        const bullets: HTMLButtonElement[] = Array.from(sliderElement.querySelectorAll('.o-slider__bullet')) as HTMLButtonElement[];
        bullets.forEach(bullet => {
            const isActive: boolean = bullet.dataset.active === 'true';
            bullet.setAttribute('tabindex', isActive ? '0' : '-1');
        });
    }

    private getActiveSliderSlide(sliderElement: HTMLElement): HTMLElement | null {
        return (sliderElement.querySelector('.o-slider__slide[data-active="true"]') as HTMLElement) ||
            (sliderElement.querySelector('.o-slider__slide') as HTMLElement);
    }

    private getActiveSliderBullet(sliderElement: HTMLElement): HTMLButtonElement | null {
        return (sliderElement.querySelector('.o-slider__bullet[data-active="true"]') as HTMLButtonElement) ||
            (sliderElement.querySelector('.o-slider__bullet') as HTMLButtonElement);
    }

    private getSliderSlideFocusableElements(slideElement: HTMLElement): HTMLElement[] {
        const selector: string = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
        const elements: HTMLElement[] = Array.from(slideElement.querySelectorAll(selector)) as HTMLElement[];
        return elements.filter(e => e.getClientRects().length > 0 && e.getAttribute('tabindex') !== '-1');
    }

    private findCardElement(element: HTMLElement): HTMLElement | null {
        return element.closest('.o-grid__item-content, .o-row__item, .o-featured__focus-target, .o-hightlight__more-info, [data-card-navigation-item="row"]') as HTMLElement | null;
    }

    private navigateRowVertical(currentRow: HTMLElement, currentElement: HTMLElement, direction: 'up' | 'down'): void {
        const highlightElement: HTMLElement | null = currentRow.classList.contains('o-hightlight')
            ? currentRow
            : (currentRow.closest('.o-hightlight') as HTMLElement | null);

        if (direction === 'down' && highlightElement && highlightElement === currentRow) {
            const firstRowItem: HTMLElement | null = this.getHighlightFirstRowItem(highlightElement);
            if (firstRowItem) {
                firstRowItem.focus();
                this.scrollIntoViewIfNeeded(firstRowItem);
                return;
            }
        }

        if (direction === 'up' && highlightElement && highlightElement !== currentRow) {
            const focusTarget: HTMLElement | null = this.getHighlightFocusTarget(highlightElement);
            if (focusTarget) {
                focusTarget.focus();
                this.scrollIntoViewIfNeeded(focusTarget);
                return;
            }
        }

        const pageRoot: HTMLElement = (currentRow.closest('[data-page-type]') as HTMLElement) || document.body;
        const currentRowWrapper: HTMLElement = this.getRowWrapper(currentRow);
        const rowWrappers: HTMLElement[] = this.getNavigableRowWrappers(pageRoot);
        if (!currentRowWrapper || !rowWrappers.length) {
            return;
        }

        const currentRowIndex: number = rowWrappers.indexOf(currentRowWrapper);
        if (currentRowIndex === -1) {
            return;
        }
        const targetRowIndex: number = direction === 'up' ? currentRowIndex - 1 : currentRowIndex + 1;
        if (targetRowIndex < 0 || targetRowIndex >= rowWrappers.length) {
            return;
        }
        const targetRowWrapper: HTMLElement = rowWrappers[targetRowIndex];

        if (direction === 'down') {
            const targetHighlight: HTMLElement | null = targetRowWrapper.querySelector('.o-hightlight') as HTMLElement | null;
            if (targetHighlight) {
                const focusTarget: HTMLElement | null = this.getHighlightFocusTarget(targetHighlight);
                if (focusTarget) {
                    focusTarget.focus();
                    this.scrollIntoViewIfNeeded(focusTarget);
                    return;
                }
            }
        }

        const currentCards: HTMLElement[] = this.getRowCards(currentRowWrapper);
        const currentCardIndex: number = currentCards.indexOf(currentElement);
        const targetCards: HTMLElement[] = this.getRowCards(targetRowWrapper);
        if (!targetCards.length) {
            return;
        }
        const targetIndex: number = currentCardIndex >= 0 ? Math.min(currentCardIndex, targetCards.length - 1) : 0;
        const targetCard: HTMLElement = targetCards[targetIndex];
        targetCard.focus();
        this.scrollIntoViewIfNeeded(targetCard);
    }

    private getHighlightFocusTarget(highlightElement: HTMLElement): HTMLElement | null {
        return highlightElement.querySelector('.o-hightlight__more-info') as HTMLElement | null;
    }

    private getHighlightFirstRowItem(highlightElement: HTMLElement): HTMLElement | null {
        return highlightElement.querySelector('.o-row__item') as HTMLElement | null;
    }

    private findNavigationContainer(element: HTMLElement): HTMLElement | null {
        let current = element;
        while (current && current !== document.body) {
            if (current.dataset.cardNavigation) {
                return current;
            }
            current = current.parentElement;
        }
        return null;
    }

    private navigateToCard(container: HTMLElement, currentElement: HTMLElement, direction: 'left' | 'right' | 'up' | 'down', navigationType: string): void {
        if (navigationType === 'grid') {
            this.navigateGrid(container, currentElement, direction);
        } 
        else if (navigationType === 'row') {
            // Only allow left/right navigation for rows
            if (direction === 'left' || direction === 'right') {
                this.navigateRow(container, currentElement, direction);
            }
        }
    }

    private navigateGrid(gridContainer: HTMLElement, currentElement: HTMLElement, direction: 'left' | 'right' | 'up' | 'down'): void {
        const cards: HTMLElement[] = Array.from(gridContainer.querySelectorAll('.o-grid__item-content')) as HTMLElement[];
        const currentIndex: number = cards.indexOf(currentElement);
        
        if (currentIndex === -1) {
            return;
        }

        // Calculate grid dimensions by analyzing CSS grid layout
        const gridItemsContainer: HTMLElement = gridContainer.querySelector('.o-grid__items');
        const gridStyle: CSSStyleDeclaration = window.getComputedStyle(gridItemsContainer);
        const gridTemplateColumns: string = gridStyle.gridTemplateColumns;

        // Extract number of columns from grid-template-columns
        let columnsCount: number = 1;
        if (gridTemplateColumns && gridTemplateColumns !== 'none') {
            columnsCount = gridTemplateColumns.split(' ').length;
        }

        let targetIndex: number = currentIndex;
        switch (direction) {
            case 'left':
                targetIndex = currentIndex > 0 ? currentIndex - 1 : cards.length - 1;
                break;
            case 'right':
                targetIndex = currentIndex < cards.length - 1 ? currentIndex + 1 : 0;
                break;
            case 'up':
                targetIndex = currentIndex - columnsCount;
                if (targetIndex < 0) {
                    // Wrap to bottom row
                    const remainingSlots: number = cards.length % columnsCount;
                    const lastRowStart: number = cards.length - (remainingSlots || columnsCount);
                    const columnPosition: number = currentIndex % columnsCount;
                    targetIndex = lastRowStart + Math.min(columnPosition, (remainingSlots || columnsCount) - 1);
                }
                break;
            case 'down':
                targetIndex = currentIndex + columnsCount;
                if (targetIndex >= cards.length) {
                    // Wrap to top row
                    const columnPosition: number = currentIndex % columnsCount;
                    targetIndex = Math.min(columnPosition, cards.length - 1);
                }
                break;
        }

        if (targetIndex >= 0 && targetIndex < cards.length && targetIndex !== currentIndex) {
            const targetCard: HTMLElement = cards[targetIndex];

            targetCard.focus();
            this.scrollIntoViewIfNeeded(targetCard);
        }
    }

    private navigateRow(rowContainer: HTMLElement, currentElement: HTMLElement, direction: 'left' | 'right'): void {
        const cards: HTMLElement[] = this.getRowCards(rowContainer);
        const currentIndex: number = cards.indexOf(currentElement);        
        if (currentIndex === -1) {
            return;
        }

        let targetIndex: number;
        switch (direction) {
            case 'left':
                targetIndex = currentIndex > 0 ? currentIndex - 1 : cards.length - 1;
                break;
            case 'right':
                targetIndex = currentIndex < cards.length - 1 ? currentIndex + 1 : 0;
                break;
            default:
                return;
        }
        if (targetIndex >= 0 && targetIndex < cards.length && targetIndex !== currentIndex) {
            const targetCard: HTMLElement = cards[targetIndex];
            targetCard.focus();
            this.scrollIntoViewIfNeeded(targetCard);
        }
    }

    private getRowCards(rowContainer: HTMLElement): HTMLElement[] {
        return Array.from(rowContainer.querySelectorAll('.o-row__item, .o-featured__focus-target, [data-card-navigation-item="row"]')) as HTMLElement[];
    }

    private getNavigableRowWrappers(pageRoot: HTMLElement): HTMLElement[] {
        const rowWrappers: HTMLElement[] = Array.from(pageRoot.querySelectorAll('.p-layout__row, .p-capsule__row, .p-collection__row, .p-partner-landing__row, .p-last-releases__row')) as HTMLElement[];
        return rowWrappers.filter(rowWrapper => this.isVisible(rowWrapper) && this.getRowCards(rowWrapper).length > 0);
    }

    private getRowWrapper(element: HTMLElement): HTMLElement | null {
        return element.closest('.p-layout__row, .p-capsule__row, .p-collection__row, .p-partner-landing__row, .p-last-releases__row') as HTMLElement | null;
    }

    private isVisible(element: HTMLElement): boolean {
        return element.getClientRects().length > 0;
    }

    private scrollIntoViewIfNeeded(element: HTMLElement): void {
        const rect: DOMRectReadOnly = element.getBoundingClientRect();
        const isInViewport: boolean = rect.top >= 0 && 
            rect.left >= 0 && 
            rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) && 
            rect.right <= (window.innerWidth || document.documentElement.clientWidth);

        if (!isInViewport) {
            element.scrollIntoView({ 
                behavior: 'smooth', 
                block: 'nearest', 
                inline: 'nearest' 
            });
        }
    }
}